/**
 * Wizard card for the microsoft_uet step (Attribution).
 *
 * The admin pastes the client's Microsoft Advertising UET Tag ID; `save_uet`
 * stores it under `seo_microsoft_uet_id`, which wires the viewer's UET base
 * tag. No secret is handled here — the Tag ID is an identifier, so it reads
 * back into the field after a refresh.
 */
import { useEffect, useRef } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useQuery } from '@tanstack/react-query'
import { CheckCircle2, Loader2, RefreshCw, Save } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useToast } from '@/hooks/use-toast'
import { supabase } from '@/lib/supabase'
import type { OnboardingTask } from '@limo/database'
import { useInvokeOnboardingStep } from '@/hooks/useOnboarding'
import { StepCard } from './StepCard'

interface MicrosoftUetCardProps {
  task: OnboardingTask
  accountId: string
  runId: string
}

interface MicrosoftUetResult {
  uetTagId?: string
}

const formSchema = z.object({
  uetTagId: z
    .string()
    .trim()
    .regex(/^\d{4,15}$/, 'UET Tag ID is the numeric ID from Microsoft Advertising'),
})

type FormData = z.infer<typeof formSchema>

export function MicrosoftUetCard({ task, accountId, runId }: MicrosoftUetCardProps) {
  const { toast } = useToast()
  const invokeStep = useInvokeOnboardingStep(runId)

  const result = (task.result ?? {}) as MicrosoftUetResult
  const isDone = task.status === 'done'

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: { uetTagId: '' },
  })

  // Read back the saved Tag ID so the field survives a refresh (not a secret).
  const { data: saved } = useQuery({
    queryKey: ['onboarding-microsoft-uet-settings', accountId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('account_settings')
        .select('value')
        .eq('account_id', accountId)
        .eq('key', 'seo_microsoft_uet_id')
        .maybeSingle()
      if (error) throw new Error(error.message)
      return data?.value ?? ''
    },
  })

  const seeded = useRef(false)
  useEffect(() => {
    if (saved == null || seeded.current) return
    seeded.current = true
    form.reset({ uetTagId: saved || result.uetTagId || '' })
  }, [saved, result.uetTagId, form])

  const uetTagId = result.uetTagId ?? saved ?? ''

  const saveUet = form.handleSubmit((values) => {
    invokeStep.mutate(
      {
        functionName: 'onboard-microsoft-uet',
        accountId,
        params: { action: 'save_uet', uetTagId: values.uetTagId },
      },
      {
        onError: (error) =>
          toast({
            title: 'Could not save UET Tag ID',
            description: error.message,
            variant: 'destructive',
          }),
      }
    )
  })

  return (
    <StepCard
      task={task}
      manualFallback={
        <div className="space-y-2">
          <p>
            Find the UET Tag ID in Microsoft Advertising &gt; Tools &gt; UET tag
            — it&rsquo;s the numeric ID (e.g. <code>12345678</code>). The viewer
            loads the UET base tag from this value automatically.
          </p>
          <p>
            This is the same value as Settings &gt; SEO &amp; Marketing &gt;
            Microsoft UET Tag ID; saving here updates that setting.
          </p>
        </div>
      }
    >
      {isDone ? (
        <div className="space-y-2 text-sm">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4 text-green-600" />
            <span>
              Microsoft UET Tag ID saved —{' '}
              <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">
                {uetTagId || '—'}
              </code>
            </span>
          </div>
          <form onSubmit={saveUet} className="flex flex-wrap items-end gap-2">
            <div className="space-y-1">
              <Label htmlFor="ms-uet-id" className="text-xs">
                Update Tag ID
              </Label>
              <Input
                id="ms-uet-id"
                className="h-8 w-48 text-xs"
                {...form.register('uetTagId')}
              />
            </div>
            <Button type="submit" size="sm" variant="outline" disabled={invokeStep.isPending}>
              {invokeStep.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="mr-2 h-4 w-4" />
              )}
              Update
            </Button>
          </form>
          {form.formState.errors.uetTagId && (
            <p className="text-xs text-red-600">{form.formState.errors.uetTagId.message}</p>
          )}
        </div>
      ) : (
        <form onSubmit={saveUet} className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Paste the client&rsquo;s Microsoft Advertising UET Tag ID.
          </p>
          <div className="space-y-1">
            <Label htmlFor="ms-uet-id">UET Tag ID</Label>
            <Input id="ms-uet-id" placeholder="e.g. 12345678" {...form.register('uetTagId')} />
            {form.formState.errors.uetTagId && (
              <p className="text-xs text-red-600">{form.formState.errors.uetTagId.message}</p>
            )}
          </div>
          <Button type="submit" size="sm" disabled={invokeStep.isPending}>
            {invokeStep.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Save className="mr-2 h-4 w-4" />
            )}
            Save UET Tag ID
          </Button>
        </form>
      )}
    </StepCard>
  )
}
